import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, SearchIcon, BellIcon, EyeIcon, EyeOffIcon, RefreshCwIcon, ShareIcon, CopyIcon, SendIcon, FileTextIcon, SmartphoneIcon, CreditCardIcon, LayoutDashboardIcon, PlusIcon, TrendingUpIcon, FileIcon, BriefcaseIcon, ShieldIcon, ArrowDownLeftIcon, ArrowUpRightIcon, XIcon, CheckCircleIcon, MessageSquareIcon, MailIcon, FingerprintIcon, ClockIcon, HandIcon, QrCodeIcon, ChevronDownIcon } from 'lucide-react';
import { NotificationPanel } from './NotificationPanel';
import { Sidebar } from './Sidebar';
import { useTheme } from '../utils/theme';
interface HomeScreenProps {
  isNewUser?: boolean;
  isMpinSet?: boolean;
  isCreditCardUser?: boolean;
  onNavigateToTransactions?: () => void;
  onNavigateToSendMoney?: () => void;
  onNavigateToPayments?: () => void;
  onNavigateToMobileTopUp?: () => void;
  onNavigateToRaast?: () => void;
  onNavigateToCards?: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToMore?: () => void;
  onLogout?: () => void;
  onSetMPIN?: () => void;
}
const RaastIcon = (props: any) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props} data-id="element-952">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" data-id="element-953" />
  </svg>;
const PRIMARY_ACTIONS = [{
  id: 'send_money',
  icon: SendIcon,
  label: 'Send \nMoney'
}, {
  id: 'bills',
  icon: FileTextIcon,
  label: 'Bills & Payments'
}, {
  id: 'topup',
  icon: SmartphoneIcon,
  label: 'Mobile\nTop-Up'
}, {
  id: 'cards',
  icon: LayoutDashboardIcon,
  label: 'More'
}];
const ALL_QUICK_ACTIONS = [{
  id: 'limit',
  label: 'Limit Management',
  icon: TrendingUpIcon
}, {
  id: 'maintenance',
  label: 'Maintenance Certificate',
  icon: FileIcon
}, {
  id: 'financing',
  label: 'Manage Financing',
  icon: BriefcaseIcon
}, {
  id: 'other_card',
  label: 'Other Bank Card',
  icon: CreditCardIcon
}, {
  id: 'auth',
  label: 'Payment Authorization',
  icon: ShieldIcon
}, {
  id: 'policy',
  label: 'Policy Briefcase',
  icon: BriefcaseIcon
}, {
  id: 'receive',
  label: 'Receive Remittance',
  icon: ArrowDownLeftIcon
}, {
  id: 'send_remit',
  label: 'Send Remittance',
  icon: ArrowUpRightIcon
}, {
  id: 'takaful',
  label: 'Takaful Claims',
  icon: ShieldIcon
}];
// Animated count-up
function useCountUp(target: number, duration = 1200) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let raf: number;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
}
export function HomeScreen({
  isNewUser = false,
  isMpinSet = false,
  isCreditCardUser = false,
  onNavigateToSendMoney,
  onNavigateToPayments,
  onNavigateToMobileTopUp,
  onNavigateToRaast,
  onNavigateToCards,
  onNavigateToProfile,
  onNavigateToMore,
  onLogout,
  onSetMPIN
}: HomeScreenProps) {
  const {
    isDark,
    isPlatinum
  } = useTheme();
  const [balanceVisible, setBalanceVisible] = useState(true);
  // Show amount on login (count-up animation runs), then auto-hide after the reveal
  useEffect(() => {
    const t = setTimeout(() => setBalanceVisible(false), 2500);
    return () => clearTimeout(t);
  }, []);
  const [taqwaDismissed, setTaqwaDismissed] = useState(false);
  const [quickActionsEditMode, setQuickActionsEditMode] = useState(false);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startLongPress = () => {
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      setQuickActionsEditMode(true);
    }, 2000);
  };
  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };
  const removeQuickAction = (id: string) => {
    setSelectedActions(prev => prev.filter(a => a !== id));
  };
  const [showShareModal, setShowShareModal] = useState(false);
  const [showAppPicker, setShowAppPicker] = useState(false);
  const [showShareSuccess, setShowShareSuccess] = useState(false);
  const [showAddActions, setShowAddActions] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrIban, setQrIban] = useState('');
  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [tempSelectedActions, setTempSelectedActions] = useState<string[]>([]);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [showCoolingPopup, setShowCoolingPopup] = useState(false);
  const [timeLeft, setTimeLeft] = useState(7200);
  const [biometricComplete, setBiometricComplete] = useState(false);
  const [biometricStep, setBiometricStep] = useState<'idle' | 'permission' | 'right_hand' | 'scanning_right' | 'left_hand' | 'scanning_left' | 'success'>('idle');
  // Rotating tagline beneath welcome heading — typewriter pattern
  const taglineList = ['Powers your bill payments in seconds.', 'Keeps your money moving, safely and securely.', 'Gives you real-time control over every rupee.', 'Simplifies transfers, anytime, anywhere.', 'Handles your payments without the wait.'];
  const [taglineIndex, setTaglineIndex] = useState(0);
  const [taglineText, setTaglineText] = useState('');
  useEffect(() => {
    const fullText = taglineList[taglineIndex];
    let timeoutId: ReturnType<typeof setTimeout>;
    const typeChar = (i: number) => {
      if (i <= fullText.length) {
        setTaglineText(fullText.slice(0, i));
        timeoutId = setTimeout(() => typeChar(i + 1), 45);
      } else {
        // Done typing — hold for 2.5s, then start erasing
        timeoutId = setTimeout(() => eraseChar(fullText.length), 2500);
      }
    };
    const eraseChar = (i: number) => {
      if (i >= 0) {
        setTaglineText(fullText.slice(0, i));
        timeoutId = setTimeout(() => eraseChar(i - 1), 30);
      } else {
        // Done erasing — brief gap, then move to next tagline
        timeoutId = setTimeout(() => {
          setTaglineIndex(idx => (idx + 1) % taglineList.length);
        }, 400);
      }
    };
    typeChar(1);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taglineIndex]);
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (showCoolingPopup && timeLeft > 0) {
      timer = setInterval(() => setTimeLeft(p => p - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [showCoolingPopup, timeLeft]);
  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor(seconds % 3600 / 60);
    const s = seconds % 60;
    return {
      h: h.toString().padStart(2, '0'),
      m: m.toString().padStart(2, '0'),
      s: s.toString().padStart(2, '0')
    };
  };
  const time = formatTime(timeLeft);
  const progress = timeLeft / 7200 * 100;
  const accountDetails = {
    bankName: 'Bank of Punjab',
    accountTitle: 'Kashish',
    accountNumber: '92302274632656',
    iban: 'PK02BOPM92302274632656'
  };
  const balanceAmount = 282992;
  const animatedBalance = useCountUp(balanceVisible ? balanceAmount : 0);
  // Carousel accounts
  const accounts = [{
    id: 'current',
    label: 'Current Account',
    amount: 282992,
    iban: 'PK02BOPM92302274632656'
  }, {
    id: 'savings',
    label: 'Savings Account',
    amount: 45500,
    iban: 'PK07BOPM92302274638821'
  }, {
    id: 'taqwa',
    label: 'BOP TAQWA Account',
    amount: 12750,
    iban: 'PK11BOPM92302274641190'
  }];
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  useEffect(() => {
    const root = carouselRef.current;
    if (!root) return;
    const updateActive = () => {
      const rootRect = root.getBoundingClientRect();
      const rootCenter = rootRect.left + rootRect.width / 2;
      let closestIdx = 0;
      let closestDist = Infinity;
      cardRefs.current.forEach((el, idx) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        const cardCenter = r.left + r.width / 2;
        const dist = Math.abs(cardCenter - rootCenter);
        if (dist < closestDist) {
          closestDist = dist;
          closestIdx = idx;
        }
      });
      setActiveCardIndex(closestIdx);
    };
    updateActive();
    root.addEventListener('scroll', updateActive, {
      passive: true
    });
    window.addEventListener('resize', updateActive);
    return () => {
      root.removeEventListener('scroll', updateActive);
      window.removeEventListener('resize', updateActive);
    };
  }, []);
  const scrollToCard = (idx: number) => {
    const root = carouselRef.current;
    if (!root) return;
    const card = cardRefs.current[idx];
    if (!card) return;
    const rootRect = root.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const scrollLeft = root.scrollLeft + (cardRect.left + cardRect.width / 2) - (rootRect.left + rootRect.width / 2);
    root.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });
  };
  const SHARE_APPS = [{
    id: 'whatsapp',
    name: 'WhatsApp',
    icon: MessageSquareIcon,
    color: '#25D366'
  }, {
    id: 'email',
    name: 'Email',
    icon: MailIcon,
    color: '#EA4335'
  }, {
    id: 'sms',
    name: 'Messages',
    icon: SmartphoneIcon,
    color: '#34B7F1'
  }, {
    id: 'copy',
    name: 'Copy Text',
    icon: CopyIcon,
    color: '#F37024'
  }];
  const handleSendMoneyClick = () => {
    onNavigateToSendMoney?.();
  };
  const handlePrimary = (id: string) => {
    if (id === 'send_money') handleSendMoneyClick();else if (id === 'raast') onNavigateToRaast?.();else if (id === 'bills') onNavigateToPayments?.();else if (id === 'topup') onNavigateToMobileTopUp?.();else if (id === 'cards') onNavigateToMore?.();
  };
  const handleOpenAddActions = () => {
    setTempSelectedActions([...selectedActions]);
    setShowAddActions(true);
  };
  const handleToggleAction = (id: string) => {
    setTempSelectedActions(prev => {
      if (prev.includes(id)) return prev.filter(a => a !== id);
      if (prev.length < 10) return [...prev, id];
      return prev;
    });
  };
  const handleSaveActions = () => {
    setSelectedActions(tempSelectedActions);
    setShowAddActions(false);
  };
  const handleAppSelect = (appId: string) => {
    const text = `Bank: ${accountDetails.bankName}\nAccount Title: ${accountDetails.accountTitle}\nAccount No: ${accountDetails.accountNumber}\nIBAN: ${accountDetails.iban}`;
    if (appId === 'copy') navigator.clipboard.writeText(text);
    setShowAppPicker(false);
    setShowShareSuccess(true);
    setTimeout(() => setShowShareSuccess(false), 2000);
  };
  const handleCopyIban = () => {
    navigator.clipboard.writeText(accountDetails.iban);
    setShowCopyToast(true);
    setTimeout(() => setShowCopyToast(false), 1500);
  };
  // ---- Theme palette ----
  const isLight = !isDark && !isPlatinum;
  const black = isLight ? '#000000' : '#F5CBA7';
  const pageBg = isLight ? '#F5F3F1' : isPlatinum ? 'linear-gradient(180deg, #1C1C1E 0%, #2A2A2C 100%)' : 'linear-gradient(180deg, #1A0E08 0%, #2D1810 100%)';
  const headerIcon = isLight ? '#7A2B06' : isPlatinum ? '#E5E5EA' : '#F5CBA7';
  const titleColor = isLight ? '#1A1A1A' : '#FFFFFF';
  const subTitle = isLight ? '#6B6B6B' : isPlatinum ? '#A0A0A5' : '#C4B5AA';
  const maroon = isLight ? '#1A0E08' : '#F5CBA7';
  const cardBg = isLight ? '#FFFFFF' : isPlatinum ? 'rgba(58, 58, 60, 0.92)' : 'rgba(61, 37, 24, 0.92)';
  return <div className="w-full min-h-screen pb-28 relative overflow-hidden" style={{
    background: '#F5F3F1'
  }} data-id="element-954">
      {/* Full-bleed dashboard background image (light mode) */}
      {isLight && <img src="https://cdn.magicpatterns.com/uploads/pktEMKrAgFLrU3nNhjFvdK/Dashboard.jpg" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none" style={{
      filter: 'blur(2px)'
    }} data-id="element-955" />}

      {/* Header */}
      <div className="relative z-10 px-6 pt-6 pb-4 flex items-center" data-id="element-956">
        <button onClick={() => setShowSidebar(true)} aria-label="Menu" className="p-2 -ml-2" data-id="element-957">
          <MenuIcon className="w-7 h-7" strokeWidth={2.2} style={{
          color: '#661806'
        }} data-id="element-958" />
        </button>

        {/* Centered logo (absolute so it's truly centered regardless of side icons) */}
        <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP" className="h-12 w-auto absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-959" />

        <div className="flex items-center gap-4 ml-auto" data-id="element-960">
          <button aria-label="Search" className="p-1 flex items-center justify-center" data-id="element-961">
            <SearchIcon style={{
            width: 20,
            height: 20,
            color: '#661806'
          }} strokeWidth={2.2} className="pr-[0px] mr-[-10px]" data-id="element-962" />
          </button>
          <button onClick={() => setShowNotifications(true)} aria-label="Notifications" className="relative p-1 flex items-center justify-center pl-[0px] pr-[0px]" data-id="element-963">
            <BellIcon style={{
            width: 20,
            height: 20,
            color: '#661806'
          }} strokeWidth={2.2} data-id="element-964" />
            <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-[#F37024] rounded-full ring-2 ring-[#F5F3F1]" data-id="element-965" />
          </button>
        </div>
      </div>

      <div className="relative z-10 px-6 space-y-6" data-id="element-966">
        {/* Welcome */}
        <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} data-id="element-967">
          <h1 className="tracking-tight font-[300] text-[20px]" style={{
          color: titleColor
        }} data-id="element-968">
            Welcome, {accountDetails.accountTitle}
          </h1>
          <div className="mt-2 flex items-center gap-1" style={{
          height: 16,
          whiteSpace: 'nowrap'
        }} data-id="element-969">
            <span style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 400,
            fontSize: 10,
            color: '#303030',
            flexShrink: 0
          }} data-id="element-970">
              digiBOP
            </span>
            <span aria-live="polite" style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 300,
            fontSize: 10,
            color: '#303030',
            display: 'inline-block',
            minWidth: 280,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'clip'
          }} data-id="element-971">
              {taglineText}
            </span>
          </div>
        </motion.div>

        {/* Glass balance card */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.45,
        delay: 0.05
      }} className="relative -mx-6" style={{
        fontFamily: "'Poppins', ui-sans-serif, system-ui, sans-serif",
        marginTop: -22
      }} data-id="element-972">
          {/* Scroll container */}
          <div ref={carouselRef} className="relative flex overflow-x-auto pb-4 pt-2 hide-scrollbar" style={{
          scrollSnapType: 'x mandatory',
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          zIndex: 1,
          paddingLeft: 50,
          paddingRight: 50,
          gap: 5,
          touchAction: 'pan-x',
          overscrollBehaviorX: 'contain'
        }} data-id="element-973">
            {accounts.map((acct, idx) => {
            const displayAmount = activeCardIndex === idx ? (idx === 0 ? animatedBalance : acct.amount).toLocaleString('en-US') + '.00' : acct.amount.toLocaleString('en-US') + '.00';
            const isActiveCard = activeCardIndex === idx;
            return <div key={acct.id} data-index={idx} ref={el => {
              cardRefs.current[idx] = el;
            }} className="relative rounded-[15px] overflow-hidden shrink-0 mt-[30px] pt-[10px] pb-[10px] pl-[10px] pr-[10px]" style={{
              width: 'calc(min(100vw, 430px) - 100px)',
              maxWidth: 350,
              minWidth: 'calc(min(100vw, 430px) - 100px)',
              height: 176,
              padding: '10px 10px',
              scrollSnapAlign: 'center',
              scrollSnapStop: 'always',
              transform: isActiveCard ? 'scale(1)' : 'scale(0.88)',
              transformOrigin: 'center center',
              opacity: isActiveCard ? 1 : 0.55,
              transition: 'transform 0.35s ease, opacity 0.35s ease, box-shadow 0.35s ease',
              background: isLight ? 'rgba(255,255,255,0.5)' : 'linear-gradient(-135deg, rgba(58,36,24,0.82) 0%, rgba(45,24,16,0.86) 100%)',
              border: '1px solid transparent',
              boxShadow: '0 4px 10px rgba(0,0,0,0.25)',
              backdropFilter: 'blur(18px) saturate(160%)',
              WebkitBackdropFilter: 'blur(18px) saturate(160%)'
            }} data-id="element-974">
                  {/* 4-corner gradient outline — 1px stroke
                   TL #F76708 → TR #FFFFFF → BR #F76708 → BL #FFFFFF */}
                  {isLight && <div aria-hidden className="absolute inset-0 rounded-[15px] pointer-events-none" style={{
                padding: '1px',
                background: 'conic-gradient(from 315deg at 50% 50%, #F76708 0deg, #FFFFFF 90deg, #F76708 180deg, #FFFFFF 270deg, #F76708 360deg)',
                WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
                zIndex: 4
              }} data-id="element-975" />}
                  {/* Inner sheen — single white gradient sweep */}
                  {isLight && <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
                background: 'linear-gradient(-135deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 60%)',
                zIndex: 1
              }} data-id="element-976" />}

                  {/* Smooth glare sweep — only on the active card */}
                  {isLight && isActiveCard && <div aria-hidden className="absolute pointer-events-none" style={{
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 2,
                overflow: 'hidden',
                borderRadius: 15
              }} data-id="element-977">
                      <div className="glare-sweep absolute" style={{
                  top: '-50%',
                  width: 70,
                  height: '200%',
                  background: 'linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.55) 50%, rgba(255,255,255,0) 100%)',
                  transform: 'rotate(-22deg)',
                  filter: 'blur(2px)'
                }} data-id="element-978" />
                    </div>}

                  {/* Top row: eye + refresh aligned right */}
                  <div className="relative flex items-center justify-end gap-[10px]" style={{
                zIndex: 3,
                height: 22
              }} data-id="element-979">
                    <button onClick={() => setBalanceVisible(!balanceVisible)} aria-label={balanceVisible ? 'Hide balance' : 'Show balance'} style={{
                  color: isLight ? '#000000' : black,
                  opacity: isLight ? 0.85 : 1
                }} data-id="element-980">
                      {balanceVisible ? <EyeIcon style={{
                    width: 20,
                    height: 20
                  }} strokeWidth={1.8} data-id="element-981" /> : <EyeOffIcon style={{
                    width: 20,
                    height: 20
                  }} strokeWidth={1.8} data-id="element-982" />}
                    </button>
                    <motion.button whileTap={{
                  rotate: 360
                }} transition={{
                  duration: 0.6
                }} aria-label="Refresh" style={{
                  color: isLight ? '#000000' : black,
                  opacity: isLight ? 0.85 : 1
                }} data-id="element-983">
                      <RefreshCwIcon style={{
                    width: 20,
                    height: 20
                  }} strokeWidth={1.8} data-id="element-984" />
                    </motion.button>
                  </div>

                  {/* Available Balance label */}
                  {!(isCreditCardUser && idx === 0) && <p style={{
                position: 'relative',
                zIndex: 3,
                fontFamily: 'Poppins, system-ui, sans-serif',
                fontWeight: 300,
                fontSize: 10,
                letterSpacing: '0.3px',
                opacity: 100
              }} className="pt-[0px] pb-[0px] mt-[-20px] mb-[0px] pl-[0px] pr-[0px] mr-[70px] text-[#000000]" data-id="element-985">
                      Available Balance
                    </p>}

                  {/* Amount — PKR + amount inline, or masked dots */}

                  {/* Available Limit mini-heading — credit card user, first card only */}
                  {isCreditCardUser && idx === 0 && <p style={{
                position: 'relative',
                zIndex: 3,
                fontFamily: 'Poppins, system-ui, sans-serif',
                fontSize: 11,
                letterSpacing: '0.4px',
                opacity: 100,
                textTransform: 'uppercase'
              }} className="pb-[0px] mt-[-20px] mb-[0px] ml-[0px] mr-[59px] font-[300] text-[#000000]" data-id="element-986">
                      Available Limit
                    </p>}

                  {/* Amount — PKR + amount inline, or masked dots */}
                  <AnimatePresence mode="wait" data-id="element-987">
                    {balanceVisible ? <motion.div key="amount-visible" initial={{
                  opacity: 0,
                  y: 4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -4
                }} transition={{
                  duration: 0.2
                }} className="relative flex items-baseline mt-[22px] gap-[6px]" style={{
                  zIndex: 3
                }} data-id="element-988">
                        <span style={{
                    fontSize: 22,
                    fontWeight: 300,
                    color: isLight ? '#5A1408' : '#F5CBA7',
                    lineHeight: 1,
                    letterSpacing: '0.5px'
                  }} data-id="element-989">
                          PKR
                        </span>
                        <h2 style={{
                    fontSize: 38,
                    color: isLight ? '#5A1408' : '#F5CBA7',
                    letterSpacing: '-1px',
                    lineHeight: 1,
                    fontFeatureSettings: '"tnum"',
                    margin: 0,
                    fontWeight: 300,
                    display: 'flex',
                    alignItems: 'baseline'
                  }} data-id="element-990">
                          {(() => {
                      const [intPart, decPart] = displayAmount.split('.');
                      return <>
                                <span data-id="element-991">{intPart}</span>
                                {decPart !== undefined && <span style={{
                          fontSize: 22,
                          fontWeight: 300,
                          letterSpacing: '0.5px'
                        }} data-id="element-992">
                                    .{decPart}
                                  </span>}
                              </>;
                    })()}
                        </h2>
                      </motion.div> : <motion.div key="amount-hidden" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.2
                }} className="relative flex items-center gap-[6px] mt-[22px]" style={{
                  zIndex: 3,
                  height: 48
                }} data-id="element-993">
                        {[...Array(8)].map((_, i) => <motion.span key={i} initial={{
                    scale: 0,
                    opacity: 0
                  }} animate={{
                    scale: 1,
                    opacity: 1
                  }} transition={{
                    delay: i * 0.04,
                    duration: 0.18
                  }} style={{
                    display: 'inline-block',
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: isLight ? '#5A1408' : '#F5CBA7',
                    opacity: 0.65,
                    flexShrink: 0
                  }} data-id="element-994" />)}
                      </motion.div>}
                  </AnimatePresence>

                  {/* IBAN row */}
                  <div className="relative flex items-end justify-between gap-[7px]" style={{
                zIndex: 3,
                position: 'absolute',
                left: 20,
                right: 10,
                bottom: 14
              }} data-id="element-995">
                    <div className="min-w-0 flex-1 ml-[-10px] mr-[0px]" data-id="element-996">
                      <p style={{
                    color: isLight ? '#3D1A0A' : '#F5CBA7',
                    opacity: 0.85,
                    marginBottom: 3,
                    letterSpacing: '0.2px'
                  }} className="text-[10px] font-[400]" data-id="element-997">
                        {isCreditCardUser && idx === 0 ? 'Card Number' : 'IBAN Number'}
                      </p>
                      <p className="truncate font-[400]" style={{
                    fontSize: 14,
                    color: isLight ? '#3D1A0A' : '#F5CBA7',
                    letterSpacing: isCreditCardUser && idx === 0 ? '1.2px' : '0.3px'
                  }} data-id="element-998">
                        {isCreditCardUser && idx === 0 ? '4242 •••• •••• 1234' : acct.iban}
                      </p>
                    </div>
                    <div className="flex items-center shrink-0 gap-[10px]" data-id="element-999">
                      <button onClick={isCreditCardUser && idx === 0 ? undefined : () => setShowShareModal(true)} aria-label="Share account" disabled={isCreditCardUser && idx === 0} style={{
                    color: isLight ? '#000000' : black,
                    opacity: isCreditCardUser && idx === 0 ? 0.4 : isLight ? 0.85 : 1,
                    cursor: isCreditCardUser && idx === 0 ? 'default' : 'pointer'
                  }} data-id="element-1000">
                        <ShareIcon style={{
                      width: 18,
                      height: 18
                    }} strokeWidth={1.8} data-id="element-1001" />
                      </button>
                      <button onClick={() => {
                    navigator.clipboard.writeText(acct.iban);
                    setShowCopyToast(true);
                    setTimeout(() => setShowCopyToast(false), 1500);
                  }} aria-label="Copy IBAN" style={{
                    color: isLight ? '#000000' : black,
                    opacity: isLight ? 0.85 : 1
                  }} data-id="element-1002">
                        <CopyIcon style={{
                      width: 18,
                      height: 18
                    }} strokeWidth={1.8} data-id="element-1003" />
                      </button>
                    </div>
                  </div>

                  {isActiveCard && !isLight && <div aria-hidden className="absolute inset-0 rounded-[15px] pointer-events-none overflow-hidden" data-id="element-1004">
                      <div className="absolute inset-0 rounded-[15px]" style={{
                  border: '1.5px solid rgba(243,112,36,0.45)'
                }} data-id="element-1005" />
                    </div>}
                </div>;
          })}
          </div>
        </motion.div>

        {/* Primary actions - 5 items */}
        <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.1
      }} data-id="element-1006">
          <div data-id="element-1007">
            <div className="grid grid-cols-4 gap-[10px]" data-id="element-1008">
              {PRIMARY_ACTIONS.map((action, idx) => {
              const Icon = action.icon;
              const isRaast = action.id === 'raast';
              return <motion.button key={action.id} onClick={() => handlePrimary(action.id)} whileTap={{
                scale: 0.95
              }} whileHover={{
                y: -2
              }} className="relative rounded-[16px] flex flex-col items-center justify-start overflow-hidden" style={{
                background: isLight ? '#FFFFFF' : cardBg,
                border: isLight ? 'none' : '1px solid rgba(243,112,36,0.12)',
                boxShadow: isLight ? '0 2px 8px rgba(0,0,0,0.06)' : '0 4px 14px rgba(0,0,0,0.25)',
                height: '90px',
                padding: '14px 6px 12px',
                gap: '8px'
              }} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.35,
                delay: 0.12 + idx * 0.05
              }} data-id="element-1009">
                    <Icon className={isRaast ? 'text-[#10B981]' : isLight ? 'text-[#F37024]' : 'text-[#F37024]'} style={{
                  width: 24,
                  height: 24
                }} strokeWidth={1.8} fill={isRaast ? '#10B981' : 'none'} data-id="element-1010" />
                    <span className="text-center leading-tight whitespace-pre-line" style={{
                  fontFamily: 'Poppins, ui-sans-serif, system-ui, sans-serif',
                  fontSize: '11px',
                  fontWeight: 500,
                  color: isLight ? '#1A1A1A' : '#FFFFFF'
                }} data-id="element-1011">
                      {action.label}
                    </span>
                  </motion.button>;
            })}
            </div>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.2
      }} data-id="element-1012">
          <div className="mb-5 flex items-center justify-between" data-id="element-1013">
            <h3 className="text-[13px] font-[500] text-[#661806]" data-id="element-1014">
              Quick Actions
            </h3>
            {quickActionsEditMode && <button onClick={() => setQuickActionsEditMode(false)} className="text-[12px] font-[500] text-[#F37024] px-3 py-1 rounded-full bg-[#F37024]/10" data-id="element-1015">
                Done
              </button>}
          </div>
          <div className="grid grid-cols-4 gap-x-3 gap-y-5 pb-2 justify-items-center" data-id="element-1016">
            {/* New action */}
            <button onClick={handleOpenAddActions} className="flex flex-col items-center gap-2 w-full max-w-[72px]" data-id="element-1017">
              <div className="w-[60px] h-[60px] rounded-full flex items-center justify-center" style={{
              background: isLight ? '#FFFFFF' : cardBg,
              boxShadow: isLight ? '0 4px 12px rgba(0,0,0,0.05)' : '0 4px 14px rgba(0,0,0,0.3)'
            }} data-id="element-1018">
                <PlusIcon style={{
                width: 24,
                height: 24,
                color: isLight ? '#7A2B06' : '#F37024'
              }} strokeWidth={2.2} data-id="element-1019" />
              </div>
              <span className="text-[11px] font-[400] text-[#000000]" data-id="element-1020">
                New action
              </span>
            </button>

            {/* Cards quick action */}
            <button onClick={onNavigateToCards} className="flex flex-col items-center gap-2 w-full max-w-[72px]" data-id="element-1021">
              <div className="w-[60px] h-[60px] rounded-full flex items-center justify-center" style={{
              background: isLight ? '#FFFFFF' : cardBg,
              boxShadow: isLight ? '0 4px 12px rgba(0,0,0,0.05)' : '0 4px 14px rgba(0,0,0,0.3)'
            }} data-id="element-1022">
                <CreditCardIcon style={{
                width: 24,
                height: 24,
                color: isLight ? '#7A2B06' : '#F37024'
              }} strokeWidth={2} data-id="element-1023" />
              </div>
              <span className="text-[11px] font-[400] text-[#000000]" data-id="element-1024">
                Cards
              </span>
            </button>

            {selectedActions.map(actionId => {
            const action = ALL_QUICK_ACTIONS.find(a => a.id === actionId);
            if (!action) return null;
            const Icon = action.icon;
            return <motion.div key={action.id} className="relative flex flex-col items-center gap-2 w-full max-w-[72px]" animate={quickActionsEditMode ? {
              rotate: [-2, 2, -2],
              transition: {
                duration: 0.25,
                repeat: Infinity,
                ease: 'easeInOut'
              }
            } : {
              rotate: 0
            }} data-id="element-1025">
                  <button onMouseDown={startLongPress} onMouseUp={cancelLongPress} onMouseLeave={cancelLongPress} onTouchStart={startLongPress} onTouchEnd={cancelLongPress} onTouchCancel={cancelLongPress} onClick={() => {
                if (quickActionsEditMode) {
                  setQuickActionsEditMode(false);
                }
              }} className="flex flex-col items-center gap-2 w-full" data-id="element-1026">
                    <div className="w-[60px] h-[60px] rounded-full flex items-center justify-center" style={{
                  background: isLight ? '#FFFFFF' : cardBg,
                  boxShadow: isLight ? '0 4px 12px rgba(0,0,0,0.05)' : '0 4px 14px rgba(0,0,0,0.3)'
                }} data-id="element-1027">
                      <Icon style={{
                    width: 24,
                    height: 24,
                    color: isLight ? '#7A2B06' : '#F37024'
                  }} strokeWidth={2} data-id="element-1028" />
                    </div>
                    <span className="text-center max-w-[80px] text-[11px] font-[400] text-[#000000]" data-id="element-1029">
                      {action.label}
                    </span>
                  </button>
                  {quickActionsEditMode && <motion.button initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} exit={{
                scale: 0
              }} onClick={e => {
                e.stopPropagation();
                removeQuickAction(action.id);
              }} aria-label={`Remove ${action.label}`} className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center z-10" style={{
                background: '#E5484D',
                boxShadow: '0 2px 6px rgba(0,0,0,0.25)'
              }} data-id="element-1030">
                      <XIcon className="w-3 h-3 text-white" strokeWidth={3} data-id="element-1031" />
                    </motion.button>}
                </motion.div>;
          })}
          </div>
        </motion.div>

        {/* BOP TAQWA banner */}
        <AnimatePresence data-id="element-1032">
          {!taqwaDismissed && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          transition: {
            duration: 0.2
          }
        }} transition={{
          duration: 0.45,
          delay: 0.25
        }} className="pl-[53px] pr-[53px]" data-id="element-1033">
              <div className="relative rounded-[20px] overflow-hidden shadow-lg flex" style={{
            background: isLight ? 'linear-gradient(110deg, #C84B11 0%, #D96A33 55%, #F4A96A 100%)' : 'linear-gradient(110deg, #F37024 0%, #E8943A 55%, #F0B488 100%)',
            width: 255,
            maxWidth: '100%',
            height: 110
          }} data-id="element-1034">
                {/* Dismiss button */}
                <button onClick={() => setTaqwaDismissed(true)} aria-label="Dismiss promotion" className="absolute top-2 right-2 z-20 w-6 h-6 rounded-full flex items-center justify-center transition-colors hover:bg-white/30" style={{
              background: 'rgba(255,255,255,0.18)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)'
            }} data-id="element-1035">
                  <XIcon className="w-3.5 h-3.5 text-white" strokeWidth={2.5} data-id="element-1036" />
                </button>
                {/* Glow */}
                <motion.div aria-hidden className="absolute pointer-events-none" style={{
              width: 180,
              height: 180,
              left: '-15%',
              top: '-40%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.28) 0%, transparent 70%)',
              filter: 'blur(20px)'
            }} animate={{
              x: [0, 10, -6, 0],
              y: [0, 8, -4, 0]
            }} transition={{
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-1037" />

                {/* Left text - 55% */}
                <div className="relative z-10 flex flex-col justify-center" style={{
              width: '55%',
              paddingLeft: 18,
              paddingRight: 8,
              paddingTop: 14,
              paddingBottom: 14
            }} data-id="element-1038">
                  <h4 className="text-white tracking-tight" style={{
                fontSize: 17,
                fontWeight: 700,
                lineHeight: 1.1
              }} data-id="element-1039">
                    BOP TAQWA
                  </h4>
                  <p style={{
                fontSize: 11,
                color: 'rgba(255,255,255,0.85)',
                marginTop: 2,
                lineHeight: 1.2
              }} data-id="element-1040">
                    Islamic Bank
                  </p>
                  <div style={{
                width: 24,
                height: 2,
                background: 'rgba(255,255,255,0.6)',
                borderRadius: 2,
                marginTop: 6,
                marginBottom: 6
              }} data-id="element-1041" />
                  <p className="text-white" style={{
                fontSize: 12,
                fontWeight: 600,
                lineHeight: 1.4
              }} data-id="element-1042">
                    Halal Banking Made
                    <br data-id="element-1043" />
                    Easy for Everyone
                  </p>
                </div>

                {/* Right illustration - 45%, flush right & bottom */}
                <div className="relative" style={{
              width: '45%'
            }} data-id="element-1044">
                  <motion.div aria-hidden className="absolute pointer-events-none" style={{
                right: 0,
                bottom: 0,
                width: '100%',
                height: 110
              }} animate={{
                y: [0, -2, 0, 2, 0]
              }} transition={{
                duration: 6,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-1045">
                    <svg viewBox="0 0 160 110" preserveAspectRatio="xMaxYMax meet" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full drop-shadow-md" data-id="element-1046">
                      <defs data-id="element-1047">
                        <linearGradient id="goldBowl" x1="0" y1="0" x2="1" y2="1" data-id="element-1048">
                          <stop offset="0" stopColor="#F4C66A" data-id="element-1049" />
                          <stop offset="0.5" stopColor="#D9982C" data-id="element-1050" />
                          <stop offset="1" stopColor="#A86F0E" data-id="element-1051" />
                        </linearGradient>
                        <linearGradient id="goldPitcher" x1="0" y1="0" x2="1" y2="1" data-id="element-1052">
                          <stop offset="0" stopColor="#FFD98A" data-id="element-1053" />
                          <stop offset="0.5" stopColor="#E5A736" data-id="element-1054" />
                          <stop offset="1" stopColor="#9A6206" data-id="element-1055" />
                        </linearGradient>
                      </defs>
                      {/* Small left vase */}
                      <ellipse cx="38" cy="104" rx="14" ry="3" fill="rgba(0,0,0,0.18)" data-id="element-1056" />
                      <path d="M30 50 Q30 46 34 46 L42 46 Q46 46 46 50 L48 64 Q50 64 50 70 L48 73 L48 100 Q48 106 38 106 Q28 106 28 100 L28 73 Q26 70 28 64 Z" fill="url(#goldPitcher)" data-id="element-1057" />
                      <path d="M32 56 Q34 64 34 78 Q32 88 34 96" stroke="rgba(255,255,255,0.45)" strokeWidth="1.5" fill="none" strokeLinecap="round" data-id="element-1058" />
                      {/* Central bowl */}
                      <ellipse cx="86" cy="104" rx="32" ry="4" fill="rgba(0,0,0,0.2)" data-id="element-1059" />
                      <path d="M56 70 Q56 100 86 102 Q116 100 116 70 Q116 64 110 62 L62 62 Q56 64 56 70 Z" fill="url(#goldBowl)" data-id="element-1060" />
                      <g stroke="rgba(255,255,255,0.5)" strokeWidth="1" fill="none" data-id="element-1061">
                        <path d="M62 72 L110 72" data-id="element-1062" />
                        <path d="M64 80 L108 80" data-id="element-1063" />
                        <path d="M68 88 L104 88" data-id="element-1064" />
                        <circle cx="72" cy="76" r="1.5" fill="rgba(255,255,255,0.55)" data-id="element-1065" />
                        <circle cx="86" cy="76" r="1.5" fill="rgba(255,255,255,0.55)" data-id="element-1066" />
                        <circle cx="100" cy="76" r="1.5" fill="rgba(255,255,255,0.55)" data-id="element-1067" />
                        <circle cx="80" cy="84" r="1.5" fill="rgba(255,255,255,0.55)" data-id="element-1068" />
                        <circle cx="92" cy="84" r="1.5" fill="rgba(255,255,255,0.55)" data-id="element-1069" />
                      </g>
                      {/* Tall right pitcher with handle */}
                      <ellipse cx="138" cy="106" rx="18" ry="3" fill="rgba(0,0,0,0.2)" data-id="element-1070" />
                      <path d="M126 36 Q126 32 130 32 L146 32 Q150 32 150 36 L152 56 Q156 56 156 64 L152 67 L152 100 Q152 108 138 108 Q124 108 124 100 L124 67 Q120 64 122 56 Z" fill="url(#goldPitcher)" data-id="element-1071" />
                      <path d="M128 44 Q130 54 130 70 Q126 82 128 94" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" fill="none" strokeLinecap="round" data-id="element-1072" />
                      <path d="M130 32 Q138 22 148 26 Q144 30 138 32 Z" fill="url(#goldPitcher)" data-id="element-1073" />
                      <circle cx="140" cy="22" r="2" fill="#F4C66A" data-id="element-1074" />
                    </svg>
                  </motion.div>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Copy toast */}
      <AnimatePresence data-id="element-1075">
        {showCopyToast && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 20
      }} className="fixed bottom-28 left-1/2 -translate-x-1/2 bg-[#1A0E08] text-white text-sm font-semibold px-5 py-3 rounded-full shadow-2xl z-50 flex items-center gap-2" data-id="element-1076">
            <CheckCircleIcon className="w-4 h-4 text-[#F37024]" strokeWidth={2.5} data-id="element-1077" />
            IBAN copied
          </motion.div>}
      </AnimatePresence>

      {/* Sidebar */}
      <Sidebar isOpen={showSidebar} onClose={() => setShowSidebar(false)} onLogout={onLogout} onNavigateToRaast={onNavigateToRaast} onNavigateToProfile={onNavigateToProfile} data-id="element-1078" />

      {/* Notifications */}
      <NotificationPanel isOpen={showNotifications} onClose={() => setShowNotifications(false)} data-id="element-1079" />

      {/* Add Quick Actions Modal */}
      <AnimatePresence data-id="element-1080">
        {showAddActions && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/40 z-[60]" onClick={() => setShowAddActions(false)} data-id="element-1081" />
            <div className="fixed bottom-0 left-0 right-0 z-[60] flex justify-center pointer-events-none" data-id="element-1082">
              <motion.div initial={{
            opacity: 0,
            y: 100
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: 100
          }} transition={{
            type: 'spring',
            damping: 25,
            stiffness: 300
          }} className="flex flex-col overflow-hidden pointer-events-auto" style={{
            width: 403,
            maxWidth: '100%',
            height: 577,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            background: '#FFFFFF',
            boxShadow: '0 -10px 40px rgba(0,0,0,0.08)',
            fontFamily: 'Poppins, system-ui, sans-serif'
          }} data-id="element-1083">
                {/* Pill handle */}
                <div className="pt-4 pb-3 flex justify-center" data-id="element-1084">
                  <div style={{
                width: 44,
                height: 5,
                borderRadius: 999,
                background: '#D6D6D6'
              }} data-id="element-1085" />
                </div>

                <div className="px-7 pt-2 pb-3" data-id="element-1086">
                  <h3 style={{
                fontFamily: 'Poppins, system-ui, sans-serif',
                color: '#1A1A1A',
                marginBottom: 6
              }} className="text-[16px] font-[600]" data-id="element-1087">
                    Add quick action(s)
                  </h3>
                  <p style={{
                fontFamily: 'Poppins, system-ui, sans-serif',
                color: '#1A1A1A'
              }} className="text-[12px] font-[300]" data-id="element-1088">
                    Pick up to 06 actions to surface on your dashboard.
                  </p>
                </div>

                <div className="flex-1 overflow-y-auto px-6 grid grid-cols-3 pt-[39px] pb-[38px] pl-[18px] pr-[18px] gap-[19px]" data-id="element-1089">
                  {ALL_QUICK_ACTIONS.map(action => {
                const Icon = action.icon;
                const selected = tempSelectedActions.includes(action.id);
                const overLimit = !selected && tempSelectedActions.length >= 6;
                return <button key={action.id} onClick={() => {
                  if (overLimit) return;
                  handleToggleAction(action.id);
                }} disabled={overLimit} className="relative flex flex-col items-center justify-center transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed pl-[14px]" style={{
                  width: 110.22,
                  height: 87.5,
                  padding: 10,
                  borderRadius: 8,
                  background: selected ? '#EFEFEF' : '#FAFAFA',
                  border: selected ? '1px solid rgba(0,0,0,0.04)' : '1px solid transparent',
                  boxShadow: selected ? 'inset 0 0 0 1px rgba(0,0,0,0.04)' : '0 1px 2px rgba(0,0,0,0.03)'
                }} data-id="element-1090">
                        <Icon style={{
                    width: 22,
                    height: 22,
                    color: '#F37024',
                    marginBottom: 6
                  }} strokeWidth={1.8} data-id="element-1091" />
                        <span style={{
                    fontFamily: 'Poppins, system-ui, sans-serif',
                    fontWeight: 400,
                    fontSize: 13,
                    lineHeight: 1.25,
                    color: '#1A1A1A',
                    textAlign: 'center'
                  }} data-id="element-1092">
                          {action.label}
                        </span>
                      </button>;
              })}
                </div>

                <div className="px-6 pt-3 pb-6" data-id="element-1093">
                  <button onClick={handleSaveActions} disabled={tempSelectedActions.length === 0} className="w-full transition-colors active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed" style={{
                height: 56,
                borderRadius: 8,
                background: '#661806',
                color: '#FFFFFF',
                fontFamily: 'Poppins, system-ui, sans-serif',
                fontWeight: 500,
                fontSize: 16,
                boxShadow: '0 6px 16px rgba(102,24,6,0.18)'
              }} data-id="element-1094">
                    Add Now
                    {tempSelectedActions.length > 0 ? ` (${tempSelectedActions.length})` : ''}
                  </button>
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>

      {/* Share Modal */}
      <AnimatePresence data-id="element-1095">
        {showShareModal && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/60 z-50" onClick={() => setShowShareModal(false)} data-id="element-1096" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} className="fixed bottom-0 left-0 right-0 z-50 max-w-[430px] mx-auto pb-[100px]" style={{
          background: '#FFFFFF',
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          boxShadow: '0 -8px 32px rgba(0,0,0,0.18)',
          padding: '28px 24px'
        }} data-id="element-1097">
              {/* Drag handle */}
              <div className="flex justify-center mb-4" data-id="element-1098">
                <div style={{
              width: 40,
              height: 4,
              borderRadius: 2,
              background: '#E0E0E0'
            }} data-id="element-1099" />
              </div>

              {/* Title */}
              <h3 style={{
            fontSize: 16,
            fontWeight: 700,
            color: '#1A1A1A',
            textAlign: 'center',
            marginBottom: 20
          }} data-id="element-1100">
                Share Account Details
              </h3>

              {/* QR Code with logo */}
              <div className="flex justify-center mb-5" data-id="element-1101">
                <div style={{
              position: 'relative',
              width: 180,
              height: 180
            }} data-id="element-1102">
                  <img src={`https://api.qrserver.com/v1/create-qr-code/?size=360x360&margin=0&ecc=H&data=${encodeURIComponent(`Bank: ${accountDetails.bankName}\nTitle: Hassan Noman\nAccount: ${accountDetails.accountNumber}\nIBAN: ${accountDetails.iban}`)}`} alt="Account details QR code" style={{
                width: 180,
                height: 180,
                display: 'block'
              }} data-id="element-1103" />
                  {/* Center logo with white background */}
                  <div aria-hidden style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 40,
                height: 40,
                background: '#FFFFFF',
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 3,
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
              }} data-id="element-1104">
                    <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP" style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }} data-id="element-1105" />
                  </div>
                </div>
              </div>

              {/* Info rows */}
              <div style={{
            marginBottom: 24
          }} data-id="element-1106">
                {[{
              label: 'Account Title',
              value: 'Hassan Noman',
              valueSize: 14
            }, {
              label: 'Account Number',
              value: accountDetails.accountNumber,
              valueSize: 14
            }, {
              label: 'IBAN',
              value: accountDetails.iban,
              valueSize: 13
            }].map((row, idx, arr) => <div key={row.label} style={{
              textAlign: 'center',
              padding: '12px 0',
              borderBottom: idx < arr.length - 1 ? '1px solid #F0F0F0' : 'none'
            }} className="pb-[1px]" data-id="element-1107">
                    <p style={{
                fontSize: 11,
                color: '#6B6B6B',
                marginBottom: 4,
                fontWeight: 400
              }} data-id="element-1108">
                      {row.label}
                    </p>
                    <p style={{
                fontSize: row.valueSize,
                fontWeight: 700,
                color: '#1A1A1A',
                letterSpacing: row.label === 'IBAN' ? '0.02em' : 'normal'
              }} data-id="element-1109">
                      {row.value}
                    </p>
                  </div>)}
              </div>

              {/* Share button */}
              <button onClick={() => {
            setShowShareModal(false);
            setShowAppPicker(true);
          }} style={{
            width: '100%',
            height: 52,
            background: '#FFFFFF',
            color: '#7A2B06',
            fontSize: 15,
            fontWeight: 600,
            borderRadius: 14,
            border: '1.5px solid #7A2B06',
            cursor: 'pointer',
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8
          }} data-id="element-1110">
                <ShareIcon style={{
              width: 18,
              height: 18
            }} strokeWidth={2} data-id="element-1111" />
                Share
              </button>

              {/* Download button */}
              <button onClick={() => {
            setShowShareModal(false);
            setShowShareSuccess(true);
            setTimeout(() => setShowShareSuccess(false), 2000);
          }} style={{
            width: '100%',
            height: 52,
            background: '#7A2B06',
            color: '#FFFFFF',
            fontSize: 15,
            fontWeight: 600,
            borderRadius: 14,
            border: 'none',
            cursor: 'pointer',
            marginBottom: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8
          }} data-id="element-1112">
                <FileTextIcon style={{
              width: 18,
              height: 18
            }} strokeWidth={2} data-id="element-1113" />
                Download
              </button>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* App Picker */}
      <AnimatePresence data-id="element-1114">
        {showAppPicker && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/60 z-50" onClick={() => setShowAppPicker(false)} data-id="element-1115" />
            <motion.div initial={{
          opacity: 0,
          y: 100
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 100
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className={`fixed bottom-0 left-0 right-0 z-50 rounded-t-[32px] p-6 ${isLight ? 'bg-white' : 'bg-[#2D1810]'}`} data-id="element-1116">
              <div className={`w-10 h-1.5 rounded-full mx-auto mb-5 ${isLight ? 'bg-[#E8D5CB]' : 'bg-[#3D2518]'}`} data-id="element-1117" />
              <h3 className="text-xl font-bold mb-5" style={{
            color: titleColor
          }} data-id="element-1118">
                Share via
              </h3>
              <div className="grid grid-cols-4 gap-3" data-id="element-1119">
                {SHARE_APPS.map(app => {
              const Icon = app.icon;
              return <button key={app.id} onClick={() => handleAppSelect(app.id)} className="flex flex-col items-center gap-2 p-3" data-id="element-1120">
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{
                  background: app.color
                }} data-id="element-1121">
                        <Icon className="w-6 h-6 text-white" strokeWidth={2} data-id="element-1122" />
                      </div>
                      <span className="text-[11px] font-medium" style={{
                  color: titleColor
                }} data-id="element-1123">
                        {app.name}
                      </span>
                    </button>;
            })}
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* Share success */}
      <AnimatePresence data-id="element-1124">
        {showShareSuccess && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 20
      }} className="fixed bottom-28 left-1/2 -translate-x-1/2 bg-[#22C55E] text-white text-sm font-semibold px-5 py-3 rounded-full shadow-2xl z-50 flex items-center gap-2" data-id="element-1125">
            <CheckCircleIcon className="w-4 h-4" strokeWidth={2.5} data-id="element-1126" />
            Shared successfully
          </motion.div>}
      </AnimatePresence>

      {/* Cooling-off modal removed — handled by dedicated CoolingOffScreen */}

      {/* Biometric overlay (kept for new users) */}
      <AnimatePresence data-id="element-1127">
        {biometricStep !== 'idle' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-[#1A0E08]/95 backdrop-blur-md z-50 flex flex-col items-center justify-center p-6" data-id="element-1128">
            {biometricStep === 'permission' && <>
                <FingerprintIcon className="w-16 h-16 text-[#F37024] mb-6" strokeWidth={1.5} data-id="element-1129" />
                <h3 className="text-2xl font-bold text-white text-center mb-3" data-id="element-1130">
                  Biometric Verification
                </h3>
                <p className="text-[#B8A89E] text-center mb-8 max-w-sm" data-id="element-1131">
                  We'll capture your fingerprints to secure your account.
                </p>
                <button onClick={() => setBiometricStep('right_hand')} className="w-full max-w-sm py-4 bg-[#F37024] hover:bg-[#D95A1A] text-white font-bold rounded-2xl transition-colors" data-id="element-1132">
                  Grant Permission
                </button>
                <button onClick={() => setBiometricStep('idle')} className="mt-3 text-[#B8A89E] text-sm" data-id="element-1133">
                  Cancel
                </button>
              </>}
            {(biometricStep === 'right_hand' || biometricStep === 'left_hand') && <>
                <HandIcon className="w-16 h-16 text-[#F37024] mb-6" strokeWidth={1.5} data-id="element-1134" />
                <h3 className="text-2xl font-bold text-white text-center mb-3" data-id="element-1135">
                  Scan your {biometricStep === 'right_hand' ? 'right' : 'left'}{' '}
                  hand
                </h3>
                <p className="text-[#B8A89E] text-center mb-8 max-w-sm" data-id="element-1136">
                  Place your fingers steadily on the sensor.
                </p>
                <button onClick={() => {
            if (biometricStep === 'right_hand') {
              setBiometricStep('scanning_right');
              setTimeout(() => setBiometricStep('left_hand'), 2500);
            } else {
              setBiometricStep('scanning_left');
              setTimeout(() => setBiometricStep('success'), 2500);
            }
          }} className="w-full max-w-sm py-4 bg-[#F37024] hover:bg-[#D95A1A] text-white font-bold rounded-2xl transition-colors" data-id="element-1137">
                  Start Scan
                </button>
              </>}
            {(biometricStep === 'scanning_right' || biometricStep === 'scanning_left') && <>
                <motion.div animate={{
            scale: [1, 1.1, 1]
          }} transition={{
            duration: 1.2,
            repeat: Infinity
          }} data-id="element-1138">
                  <FingerprintIcon className="w-20 h-20 text-[#F37024] mb-6" strokeWidth={1.5} data-id="element-1139" />
                </motion.div>
                <h3 className="text-2xl font-bold text-white text-center" data-id="element-1140">
                  Scanning...
                </h3>
              </>}
            {biometricStep === 'success' && <>
                <div className="w-16 h-16 rounded-full bg-[#22C55E] flex items-center justify-center mb-6" data-id="element-1141">
                  <CheckCircleIcon className="w-10 h-10 text-white" strokeWidth={2.5} data-id="element-1142" />
                </div>
                <h3 className="text-2xl font-bold text-white text-center mb-3" data-id="element-1143">
                  All set!
                </h3>
                <p className="text-[#B8A89E] text-center mb-8 max-w-sm" data-id="element-1144">
                  Biometric verification complete.
                </p>
                <button onClick={() => {
            setBiometricStep('idle');
            setBiometricComplete(true);
            onSetMPIN?.();
          }} className="w-full max-w-sm py-4 bg-[#F37024] hover:bg-[#D95A1A] text-white font-bold rounded-2xl transition-colors" data-id="element-1145">
                  Continue
                </button>
              </>}
          </motion.div>}
      </AnimatePresence>

      {/* QR Code Bottom Sheet Modal */}
      <AnimatePresence data-id="element-1146">
        {showQRModal && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25
        }} onClick={() => setShowQRModal(false)} className="fixed inset-0 z-50" style={{
          background: 'rgba(0,0,0,0.5)'
        }} data-id="element-1147" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} className="fixed left-0 right-0 bottom-0 z-50 max-w-[430px] mx-auto" style={{
          background: '#FFFFFF',
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          boxShadow: '0 -8px 32px rgba(0,0,0,0.18)',
          paddingBottom: 32
        }} data-id="element-1148">
              {/* Drag handle */}
              <div className="flex justify-center pt-3 pb-1" data-id="element-1149">
                <div style={{
              width: 40,
              height: 4,
              borderRadius: 2,
              background: 'rgba(26,26,26,0.15)'
            }} data-id="element-1150" />
              </div>

              {/* Close button (top) */}
              <div className="flex items-center justify-between px-5 pt-2 pb-4" data-id="element-1151">
                <span style={{
              fontSize: 16,
              fontWeight: 600,
              color: '#1A1A1A'
            }} data-id="element-1152">
                  Scan QR Code
                </span>
                <button onClick={() => setShowQRModal(false)} aria-label="Close" className="p-1.5 rounded-full" style={{
              background: 'rgba(26,26,26,0.06)'
            }} data-id="element-1153">
                  <ChevronDownIcon style={{
                width: 20,
                height: 20,
                color: '#1A1A1A'
              }} strokeWidth={2} data-id="element-1154" />
                </button>
              </div>

              {/* QR Code */}
              <div className="flex flex-col items-center px-6 pt-2 pb-[80px]" data-id="element-1155">
                <div style={{
              padding: 16,
              borderRadius: 20,
              background: '#FFFFFF',
              border: '1px solid rgba(26,26,26,0.08)',
              boxShadow: '0 4px 16px rgba(26,26,26,0.06)'
            }} data-id="element-1156">
                  <img src={`https://api.qrserver.com/v1/create-qr-code/?size=260x260&margin=0&data=${encodeURIComponent(qrIban)}`} alt={`QR code for IBAN ${qrIban}`} style={{
                width: 240,
                height: 240,
                display: 'block'
              }} data-id="element-1157" />
                </div>

                <p style={{
              marginTop: 20,
              fontSize: 11,
              fontWeight: 500,
              color: 'rgba(26,26,26,0.55)',
              letterSpacing: '0.04em',
              textTransform: 'uppercase'
            }} data-id="element-1158">
                  IBAN Number
                </p>
                <p style={{
              marginTop: 4,
              fontSize: 13,
              color: '#1A1A1A',
              fontFamily: "'Poppins', ui-sans-serif, system-ui, sans-serif",
              letterSpacing: '0.02em'
            }} data-id="element-1159">
                  {qrIban}
                </p>

                <button onClick={() => {
              navigator.clipboard.writeText(qrIban);
              setShowCopyToast(true);
              setTimeout(() => setShowCopyToast(false), 1500);
            }} className="mt-5 flex items-center gap-2 px-4 py-2 rounded-full" style={{
              background: 'rgba(200,75,17,0.08)',
              color: '#F37024',
              fontSize: 13,
              fontWeight: 600
            }} data-id="element-1160">
                  <CopyIcon style={{
                width: 14,
                height: 14
              }} strokeWidth={2} data-id="element-1161" />
                  Copy IBAN
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}